import axios from '../../app/axios';
import type { components } from './swagger';
import {
  GetProvidersParams,
  GetProvidersResponse,
  GetLocationsParams,
  GetLocationsResponse,
  NotificationApiParams,
  NotificationResponse,
  NotificationCountParams,
  NotificationCountResponse,
  NotificationModulesResponse,
  NotificationEventsResponse,
  NotificationUnreadCountResponse,
  NotificationGenericResponse,
  SearchUsersParams,
  SearchUsersResponse,
} from '../types/commonTypes';

/**
 * Wire-level response shapes pulled from the generated `swagger.d.ts`
 * (regenerate with `npm run gen:types`; source defaults to
 * https://preprod-portalservice.hellohealth.com/v3/api-docs and can be
 * overridden via the SWAGGER_URL env var).
 *
 * Endpoint mapping:
 *
 * Method  Path                                                          Operation                       Wire response
 * ──────  ───────────────────────────────────────────────────────────── ──────────────────────────────  ───────────────────────────────────────────
 * GET     /api/selectProviders                                          selectProviders                 APIResponseObject
 * GET     /api/fetchProvider                                            selectProvider                  APIResponseUserDTO
 * GET     /api/listLocation                                             listLocation                    APIResponsePortalAccountLocationDTO
 * GET     /api/listNotification                                         listNotification                APIResponseResBroadcastMessageDTO
 * GET     /api/notificationCount                                        notificationCount               APIResponseObject
 * GET     /api/notifications/user/{userId}                              getNotificationsByUserId        APIResponseNotificationEventResponseDto
 * GET     /api/notifications/application-modules/{userId}               getModulesForUser               APIResponseApplicationModuleDTO
 * GET     /api/notifications/count/unread                               getUnreadCount                  APIResponseLong
 * PUT     /api/notifications/{notificationId}/user/{userId}/read-status markNotificationAsRead          APIResponseNotificationEventDetailDto
 * PUT     /api/notifications/user/{userId}/mark-all-read                markAllNotificationsAsRead      APIResponseString
 * GET     /api/searchUsers                                              getHhPatients                   APIResponseUserDTO
 *
 * Notes:
 * - `selectProviders` and `notificationCount` are typed upstream as
 *   `APIResponseObject` (i.e. the generic envelope). The local
 *   `GetProvidersResponse` / `NotificationCountResponse` are stricter
 *   projections; we cast at the boundary with `as unknown as <Local>`.
 * - `markAllNotificationsAsRead` is typed as `APIResponseString` upstream;
 *   the local `NotificationGenericResponse` captures the runtime envelope.
 */
type Schemas = components['schemas'];

export const getProvidersApi = async (
  params: GetProvidersParams
): Promise<GetProvidersResponse> => {
  const response = await axios.get<Schemas['APIResponseObject']>('/selectProviders', {
    params: {
      portalAccountId: params.portalAccountId,
      allowApptScheduleConfig: params.allowApptScheduleConfig,
      userId: params.userId,
    },
  });

  return response.data as unknown as GetProvidersResponse;
};

// Fetch providers from /fetchProvider — returns the full provider list for a
// portal account (used by the message-compose recipient dropdown).
export const fetchProvidersApi = async (
  params: { portalAccountId: number | null; userId: number }
): Promise<GetProvidersResponse> => {
  const response = await axios.get<Schemas['APIResponseUserDTO']>('/fetchProvider', {
    params: {
      portalAccountId: params.portalAccountId,
      userId: params.userId,
    },
  });

  return response.data as unknown as GetProvidersResponse;
};

export const getLocationsApi = async (
  params: GetLocationsParams
): Promise<GetLocationsResponse> => {
  const response = await axios.get<Schemas['APIResponsePortalAccountLocationDTO']>(
    '/listLocation',
    {
      params: {
        portalAccountId: params.portalAccountId,
        userId: params.userId,
      },
    }
  );

  return response.data as unknown as GetLocationsResponse;
};

export const getNotificationsApi = async (
  params: NotificationApiParams
): Promise<NotificationResponse> => {
  const response = await axios.get<Schemas['APIResponseResBroadcastMessageDTO']>(
    '/listNotification',
    {
      params: {
        userId: params.userId,
        pageNo: params.pageNo,
        pageSize: params.pageSize,
        readStatus: params.readStatus,
      },
    }
  );

  return response.data as unknown as NotificationResponse;
};

export const getNotificationCountApi = async (
  params: NotificationCountParams
): Promise<NotificationCountResponse> => {
  const response = await axios.get<Schemas['APIResponseObject']>('/notificationCount', {
    params: {
      userId: params.userId,
      isRead: params.isRead,
    },
  });

  return response.data as unknown as NotificationCountResponse;
};

export const getNotificationEventsApi = async (
  userId: number,
  page = 0,
  size = 20,
  moduleId?: number
): Promise<NotificationEventsResponse> => {
  const response = await axios.get<Schemas['APIResponseNotificationEventResponseDto']>(
    `/notifications/user/${userId}`,
    {
      params: { page, size, ...(moduleId ? { moduleId } : {}) },
    }
  );

  return response.data as unknown as NotificationEventsResponse;
};

export const getNotificationModulesApi = async (
  userId: number
): Promise<NotificationModulesResponse> => {
  const response = await axios.get<Schemas['APIResponseApplicationModuleDTO']>(
    `/notifications/application-modules/${userId}`
  );

  return response.data as unknown as NotificationModulesResponse;
};

export const getNotificationUnreadCountApi = async (
  userId: number
): Promise<NotificationUnreadCountResponse> => {
  const response = await axios.get<Schemas['APIResponseLong']>(
    '/notifications/count/unread',
    { params: { userId } }
  );

  return response.data as unknown as NotificationUnreadCountResponse;
};

export const markNotificationReadApi = async (
  notificationId: number,
  userId: number
): Promise<void> => {
  await axios.put<Schemas['APIResponseNotificationEventDetailDto']>(
    `/notifications/${notificationId}/user/${userId}/read-status`,
    null,
    { params: { readStatus: true } }
  );
};

export const markAllNotificationsReadApi = async (
  userId: number
): Promise<NotificationGenericResponse> => {
  const response = await axios.put<Schemas['APIResponseString']>(
    `/notifications/user/${userId}/mark-all-read`
  );

  return response.data as unknown as NotificationGenericResponse;
};

export const searchUsersApi = async (
  params: SearchUsersParams
): Promise<SearchUsersResponse> => {
  const response = await axios.get<Schemas['APIResponseUserDTO']>('/searchUsers', {
    params: {
      portalAccountId: params.portalAccountId,
      searchParam: params.searchParam,
      userId: params.userId,
    },
  });

  return response.data as unknown as SearchUsersResponse;
};
